import { CloseBold, InformationCircleBold } from '@ha/icons';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import {
  Tooltip,
  SwipeableDrawer,
  IconButton,
  Divider,
  Grid,
  units,
} from '@hbf/dsl';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { track } from '@hbf/analytics';

interface Props {
  text: React.ReactNode;
  title: React.ReactNode;
  analyticsName: string;
}

const useStyles = makeStyles()(theme => ({
  drawerPaper: {
    borderTopLeftRadius: '16px',
    borderTopRightRadius: '16px',
  },
  tooltipCard: {
    padding: units.rem(1),
    margin: 0,
  },
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[1],
    fontSize: theme.typography.fontSize,
  },
  divider: {
    marginLeft: units.rem(-1),
    marginRight: units.rem(-1),
  },
  popper: {
    opacity: 1,
  },
  icon: {
    padding: 0,
    paddingLeft: '12px',
  },
}));

const SearchFiltersTooltip: React.FC<Props> = ({
  title,
  text,
  analyticsName,
}) => {
  const [open, setOpen] = React.useState(false);
  const { md } = useMediaQuery();
  const { classes, cx } = useStyles();

  const handleTooltipClose = React.useCallback(e => {
    e.stopPropagation();
    setOpen(false);
  }, []);
  const handleTooltipOpen = React.useCallback(
    e => {
      e.stopPropagation();

      setOpen(true);

      track('Tooltip opened', {
        name: analyticsName,
        opened_from: 'search',
      });
    },
    [analyticsName],
  );

  return md ? (
    <Tooltip
      classes={{
        tooltip: cx(classes.tooltip, classes.tooltipCard),
        popper: classes.popper,
      }}
      PopperProps={{ disablePortal: true }}
      placement="right"
      open={open}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      title={<div>{text}</div>}
    >
      <IconButton
        onMouseEnter={handleTooltipOpen}
        onMouseLeave={handleTooltipClose}
        className={classes.icon}
        size="large"
      >
        <InformationCircleBold color="disabled" fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : (
    <React.Fragment>
      <IconButton
        onClick={handleTooltipOpen}
        className={classes.icon}
        size="large"
      >
        <InformationCircleBold color="disabled" fontSize="small" />
      </IconButton>
      <SwipeableDrawer
        anchor="bottom"
        PaperProps={{ className: cx(classes.drawerPaper, classes.tooltipCard) }}
        open={open}
        onClose={handleTooltipClose}
        onOpen={handleTooltipOpen}
      >
        <Grid container justifyContent="space-between" alignItems="center">
          {title}
          <IconButton onClick={handleTooltipClose} size="large">
            <CloseBold fontSize="small" />
          </IconButton>
        </Grid>
        <Divider kind="spaced-m" className={classes.divider} />
        {text}
      </SwipeableDrawer>
    </React.Fragment>
  );
};

export { SearchFiltersTooltip };
